import * as React from 'react';
import { cn } from '@/lib/utils';
import { FieldAccessibilityContext, fieldAttributes, type FieldAccessibility } from './field-accessibility';

/**
 * Sober DS (v2) — FormField. Wraps a control with a bold label, optional
 * required marker, helper text and error text. Error supersedes helper.
 * Tokens only: so-12 type, sober-text label, sober-secondary helper,
 * sober-red required marker + error.
 */
export interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  htmlFor?: string;
  required?: boolean;
  helper?: string;
  error?: string;
  children: React.ReactNode;
}

export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  ({ className, label, htmlFor, required, helper, error, children, ...props }, ref) => {
    const generatedId = React.useId();
    const explicitId = htmlFor ?? findControlId(children);
    const id = explicitId ?? generatedId;
    const field: FieldAccessibility = {
      id, labelId: `${id}-label`, explicit: Boolean(explicitId), required, invalid: Boolean(error),
      descriptionId: error || helper ? `${id}-description` : undefined,
    };
    return (
    <FieldAccessibilityContext.Provider value={field}>
    <div ref={ref} className={cn('flex flex-col gap-1.5', className)} {...props}>
      <label id={field.labelId} htmlFor={id} className="text-so-14 font-bold text-sober-text">
        {label}
        {required ? <span aria-hidden="true" className="text-sober-red"> *</span> : null}
      </label>
      {connectNativeControls(children, field)}
      {error ? (
        <p id={field.descriptionId} role="alert" className="text-so-14 text-sober-red">{error}</p>
      ) : helper ? (
        <p id={field.descriptionId} className="text-so-14 text-sober-secondary">{helper}</p>
      ) : null}
    </div>
    </FieldAccessibilityContext.Provider>
    );
  }
);
FormField.displayName = 'FormField';

function findControlId(children: React.ReactNode): string | undefined {
  for (const child of React.Children.toArray(children)) {
    if (!React.isValidElement<{ id?: string; children?: React.ReactNode }>(child)) continue;
    if (child.props.id && (typeof child.type !== 'string' || ['input', 'select', 'textarea', 'button'].includes(child.type))) return child.props.id;
    const nested = findControlId(child.props.children);
    if (nested) return nested;
  }
}

// Native controls do not consume context; keep legacy wrappers and fragments working.
function connectNativeControls(children: React.ReactNode, field: FieldAccessibility): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement<React.InputHTMLAttributes<HTMLInputElement>>(child)) return child;
    if (typeof child.type === 'string' && ['input', 'select', 'textarea'].includes(child.type)) {
      return React.cloneElement(child, fieldAttributes(field, child.props));
    }
    if ((typeof child.type === 'string' || child.type === React.Fragment) && child.props.children) {
      return React.cloneElement(child, {}, connectNativeControls(child.props.children, field));
    }
    return child;
  });
}
